import jsPDF from 'jspdf'
import type {FormData} from '../components/constants'
import {CATEGORY_OPTIONS} from '../components/constants'
import type {PolicySection} from '../hooks/usePolicyGenerator'

// ─── Font ─────────────────────────────────────────────────────────────────────

const FONT_NAME = 'Systemia'

async function loadFonts(doc: jsPDF) {
  const toBase64 = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf)
    let binary = ''

    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i])
    }

    return btoa(binary)
  }

  const [regular, bold] = await Promise.all([
    fetch('/fonts/Systemia-Regular.ttf').then((r) => r.arrayBuffer()),
    fetch('/fonts/Systemia-Bold.ttf').then((r) => r.arrayBuffer()),
  ])

  doc.addFileToVFS('Systemia-Regular.ttf', toBase64(regular))
  doc.addFont('Systemia-Regular.ttf', FONT_NAME, 'normal')
  doc.addFileToVFS('Systemia-Bold.ttf', toBase64(bold))
  doc.addFont('Systemia-Bold.ttf', FONT_NAME, 'bold')
  doc.setFont(FONT_NAME, 'normal')
}

// ─── Colors ───────────────────────────────────────────────────────────────────

const COVER_BG: [number, number, number] = [10, 34, 24]
const FOOTER_BG: [number, number, number] = [0, 36, 20]
const WHITE: [number, number, number] = [255, 255, 255]
const NEON: [number, number, number] = [180, 255, 68]
const BODY_TEXT: [number, number, number] = [50, 50, 50]
const HEADING_COLOR: [number, number, number] = [19, 18, 18]
const SUBHEADING_COLOR: [number, number, number] = [30, 30, 30]
const HIGHLIGHT_COLOR: [number, number, number] = [0, 110, 55]
const LIGHT_BORDER: [number, number, number] = [225, 225, 225]
const GREY: [number, number, number] = [139, 139, 139]

// ─── Logo ─────────────────────────────────────────────────────────────────────

function svgToPng(svgString: string, width: number, height: number): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const scale = 3
    canvas.width = width * scale
    canvas.height = height * scale
    const ctx = canvas.getContext('2d')!
    const img = new Image()

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/png'))
    }

    img.src = `data:image/svg+xml;base64,${btoa(svgString)}`
  })
}

async function addLogo(doc: jsPDF, x: number, y: number, width: number) {
  const height = width * (19 / 79)

  const svgText = await fetch('/icons/moss-logo-v2.svg')
    .then((r) => r.text())
    .then((s) => s.replace(/currentColor/g, 'white'))

  // Pass 3× dimensions so the canvas renders at 9× the PDF size (sharp at retina/print quality)
  const pngData = await svgToPng(svgText, width * 3, height * 3)
  doc.addImage(pngData, 'PNG', x, y, width, height)
}

// ─── Footer / page break ──────────────────────────────────────────────────────

function addFooter(doc: jsPDF, companyName: string) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  doc.setFillColor(...FOOTER_BG)
  doc.rect(0, pageHeight - 14, pageWidth, 14, 'F')
  doc.setFontSize(7)
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(...WHITE)
  const date = new Date().toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})
  doc.text(`${companyName || 'Expense Policy'}  |  Generated ${date}`, 20, pageHeight - 5)
  doc.text('getmoss.com', pageWidth - 20, pageHeight - 5, {align: 'right'})
}

function checkPageBreak(doc: jsPDF, y: number, needed: number, margin: number, companyName: string): number {
  const pageHeight = doc.internal.pageSize.getHeight()

  if (y + needed > pageHeight - 20) {
    addFooter(doc, companyName)
    doc.addPage()
    return margin
  }

  return y
}

// ─── Content parsing ──────────────────────────────────────────────────────────

type Segment = {text: string; bold: boolean; highlight: boolean}
type ParsedLine =
  {kind: 'subheading'; text: string} | {kind: 'bullet'; segments: Segment[]} | {kind: 'paragraph'; segments: Segment[]}

function parseInline(raw: string): Segment[] {
  // Normalise HTML inline elements to markdown markers
  const text = raw
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<mark>(.*?)<\/mark>/gi, '==$1==')
    .replace(/<[^>]+>/g, '') // strip any remaining tags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')

  const segments: Segment[] = []
  const re = /(\*\*(.+?)\*\*|==(.+?)==)/g
  let last = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      segments.push({text: text.slice(last, match.index), bold: false, highlight: false})
    }

    if (match[0].startsWith('**')) {
      segments.push({text: match[2], bold: true, highlight: false})
    } else {
      segments.push({text: match[3], bold: false, highlight: true})
    }

    last = match.index + match[0].length
  }

  if (last < text.length) {
    segments.push({text: text.slice(last), bold: false, highlight: false})
  }

  return segments.filter((s) => s.text.length > 0)
}

function parsePolicyToLines(policyText: string): ParsedLine[] {
  const isHtml = policyText.trimStart().startsWith('<')
  const lines: ParsedLine[] = []

  if (isHtml) {
    // Normalise HTML to a line-based format we can parse
    const normalized = policyText
      .replace(/<h6>(.*?)<\/h6>/gi, '\n__H6__$1\n')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<ul>/gi, '')
      .replace(/<li>(.*?)<\/li>/gi, '\n__LI__$1\n')
      .replace(/<p>(.*?)<\/p>/gi, '\n$1\n')

    for (const line of normalized.split('\n')) {
      const trimmed = line.trim()

      if (!trimmed) {
        continue
      }

      if (trimmed.startsWith('__H6__')) {
        lines.push({
          kind: 'subheading',
          text: trimmed
            .slice(6)
            .replace(/<[^>]+>/g, '')
            .trim(),
        })
      } else if (trimmed.startsWith('__LI__')) {
        lines.push({kind: 'bullet', segments: parseInline(trimmed.slice(6))})
      } else {
        lines.push({kind: 'paragraph', segments: parseInline(trimmed)})
      }
    }
  } else {
    // Markdown
    const stripped = policyText.replace(/^####[^\n]*\n*/, '')

    for (const line of stripped.split('\n')) {
      const trimmed = line.trim()

      if (!trimmed) {
        continue
      }

      if (trimmed.startsWith('###### ')) {
        lines.push({kind: 'subheading', text: trimmed.slice(7)})
      } else if (/^[-*]\s+/.test(trimmed)) {
        lines.push({kind: 'bullet', segments: parseInline(trimmed.replace(/^[-*]\s+/, ''))})
      } else if (!trimmed.startsWith('#')) {
        lines.push({kind: 'paragraph', segments: parseInline(trimmed)})
      }
    }
  }

  return lines
}

// ─── Inline text renderer with word-wrap ──────────────────────────────────────

/**
 * Renders segments with inline bold/highlight support, wrapping at maxWidth.
 * Returns the y coordinate after the last rendered line.
 */
function renderSegments(
  doc: jsPDF,
  segments: Segment[],
  startX: number,
  startY: number,
  maxWidth: number,
  fontSize: number,
  lineHeight: number,
): number {
  doc.setFontSize(fontSize)

  // Tokenise into words preserving which segment each word came from
  type Word = {text: string; bold: boolean; highlight: boolean}
  const words: Word[] = []

  for (const seg of segments) {
    const parts = seg.text.split(/(\s+)/)

    for (const part of parts) {
      if (!part) {
        continue
      }

      words.push({text: part, bold: seg.bold, highlight: seg.highlight})
    }
  }

  // Build visual lines respecting maxWidth
  type VisualLine = Word[]
  const visualLines: VisualLine[] = []
  let current: Word[] = []
  let currentWidth = 0

  const measureWord = (w: Word): number => {
    doc.setFont(FONT_NAME, w.bold || w.highlight ? 'bold' : 'normal')
    return doc.getTextWidth(w.text)
  }

  doc.setFont(FONT_NAME, 'normal')
  const spaceWidth = doc.getTextWidth(' ')

  for (const word of words) {
    if (/^\s+$/.test(word.text)) {
      continue // skip whitespace tokens — we add spaces ourselves
    }

    const ww = measureWord(word)
    const needed = current.length === 0 ? ww : currentWidth + spaceWidth + ww

    if (needed > maxWidth && current.length > 0) {
      visualLines.push(current)
      current = [word]
      currentWidth = ww
    } else {
      current.push(word)
      currentWidth = needed
    }
  }

  if (current.length > 0) {
    visualLines.push(current)
  }

  let y = startY

  for (const vline of visualLines) {
    let x = startX

    for (let i = 0; i < vline.length; i++) {
      const w = vline[i]
      doc.setFont(FONT_NAME, w.bold || w.highlight ? 'bold' : 'normal')
      doc.setTextColor(...(w.highlight ? HIGHLIGHT_COLOR : BODY_TEXT))
      doc.text(w.text, x, y)
      const ww = doc.getTextWidth(w.text)
      x += ww + (i < vline.length - 1 ? spaceWidth : 0)
    }

    y += lineHeight
  }

  // Reset
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(...BODY_TEXT)

  return y
}

// ─── Label helpers ────────────────────────────────────────────────────────────

const allCategories = CATEGORY_OPTIONS.flatMap((g) => g.categories)

function getCategoryLabel(id: string): string {
  if (id === 'intro') {
    return 'Introduction'
  }

  if (id === 'summary') {
    return 'Summary'
  }

  return allCategories.find((c) => c.value === id)?.label ?? id
}

// ─── Main export ──────────────────────────────────────────────────────────────

export async function exportPolicyPdf(
  sections: PolicySection[],
  formData: FormData,
  orderedIds: string[],
): Promise<void> {
  const doc = new jsPDF()
  await loadFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentWidth = pageWidth - margin * 2

  // ── Cover page ──────────────────────────────────────────────────────────────

  doc.setFillColor(...COVER_BG)
  doc.rect(0, 0, pageWidth, pageHeight, 'F')

  // Logo top-left
  await addLogo(doc, margin, 18, 32)

  // Company name
  const companyName = formData.companyName || 'Your Company'
  doc.setFont(FONT_NAME, 'bold')
  doc.setTextColor(...WHITE)

  // Scale font to fit company name on one line (max ~56pt)
  let nameFontSize = 56
  doc.setFontSize(nameFontSize)

  while (doc.getTextWidth(companyName) > contentWidth && nameFontSize > 24) {
    nameFontSize -= 2
    doc.setFontSize(nameFontSize)
  }

  // Eyebrow sits 10mm above the company name baseline
  // 1pt ≈ 0.3527mm; cap height ≈ 72% of font size
  const nameCapHeightMm = nameFontSize * 0.3527 * 0.72
  const nameY = pageHeight * 0.3 // company name baseline at ~30% from top
  const eyebrowY = nameY - nameCapHeightMm - 6 // above the top of the letters

  doc.setFontSize(9)
  doc.setFont(FONT_NAME, 'bold')
  doc.setTextColor(...NEON)
  doc.text('EXPENSE POLICY', margin, eyebrowY)

  // Company name
  doc.setFontSize(nameFontSize)
  doc.setFont(FONT_NAME, 'bold')
  doc.setTextColor(...WHITE)
  doc.text(companyName, margin, nameY)

  // Description — below the descender of the company name
  const descenderMm = nameFontSize * 0.3527 * 0.28
  const descY = nameY + descenderMm + 10

  doc.setFontSize(12)
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(255, 255, 255)
  const gState = doc.GState({opacity: 0.45})
  doc.saveGraphicsState()
  doc.setGState(gState)

  doc.text(
    'A tailored spend policy covering approval thresholds, categories, limits, and submission requirements.',
    margin,
    descY,
    {maxWidth: contentWidth},
  )

  doc.restoreGraphicsState()

  // Categories list — placed right below the description
  const categoryIds = orderedIds.filter((id) => id !== 'intro' && id !== 'summary')

  if (categoryIds.length > 0) {
    const categoryLabels = categoryIds.map((id) => getCategoryLabel(id))
    const separator = '  ·  '

    doc.setFontSize(9)
    doc.setFont(FONT_NAME, 'normal')

    const catLines: string[] = []
    let currentLine = ''

    for (const lbl of categoryLabels) {
      const candidate = currentLine ? currentLine + separator + lbl : lbl

      if (doc.getTextWidth(candidate) > contentWidth && currentLine) {
        catLines.push(currentLine)
        currentLine = lbl
      } else {
        currentLine = candidate
      }
    }

    if (currentLine) {
      catLines.push(currentLine)
    }

    // Approx height of description text (12pt over ~2 lines)
    const descHeightMm = 12 * 0.3527 * 2.4
    const labelY = descY + descHeightMm + 10

    // "COVERED CATEGORIES" label
    doc.setFontSize(7)
    doc.setFont(FONT_NAME, 'bold')
    doc.setTextColor(...NEON)
    doc.text('COVERED CATEGORIES', margin, labelY)

    const catStartY = labelY + 5

    const catGState = doc.GState({opacity: 0.5})
    doc.saveGraphicsState()
    doc.setGState(catGState)
    doc.setTextColor(...WHITE)

    catLines.forEach((line, i) => {
      doc.text(line, margin, catStartY + i * 6)
    })

    doc.restoreGraphicsState()
  }

  // Footer line on cover
  doc.setFillColor(...FOOTER_BG)
  doc.rect(0, pageHeight - 14, pageWidth, 14, 'F')
  doc.setFontSize(7)
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(...WHITE)
  const date = new Date().toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})
  doc.text(`Generated with Moss Expense Policy Generator  |  ${date}`, margin, pageHeight - 5)
  doc.text('getmoss.com', pageWidth - margin, pageHeight - 5, {align: 'right'})

  // ── Content pages ───────────────────────────────────────────────────────────

  doc.addPage()
  let y = margin

  const orderedSections = orderedIds
    .map((id) => sections.find((s) => s.id === id))
    .filter((s): s is PolicySection => !!s)

  let currentGroup: string | null = null

  for (const section of orderedSections) {
    // Use the actual heading from policyText (same logic as the editor's Editable component)
    const label = section.policyText.match(/^#{1,6}\s+(.+)/)?.[1] ?? getCategoryLabel(section.id)
    const parsedLines = parsePolicyToLines(section.policyText)

    // Group heading divider for category sections (not intro/summary)
    const group = CATEGORY_OPTIONS.find((g) => g.categories.some((c) => c.value === section.id))

    if (group && group.section !== currentGroup) {
      currentGroup = group.section
      y = checkPageBreak(doc, y, 18, margin, companyName)

      if (y > margin + 2) {
        y += 6 // extra top gap (skip on very first item)
      }

      // Group label + line
      doc.setFontSize(7)
      doc.setFont(FONT_NAME, 'bold')
      doc.setTextColor(...GREY)
      doc.text(group.sectionLabel.toUpperCase(), margin, y)
      const labelWidth = doc.getTextWidth(group.sectionLabel.toUpperCase())
      doc.setDrawColor(...LIGHT_BORDER)
      doc.setLineWidth(0.3)
      doc.line(margin + labelWidth + 4, y - 1, margin + contentWidth, y - 1)
      y += 8
    }

    // Estimate section height to avoid orphan headings
    const estimatedHeight = 10 + 4 + parsedLines.length * 5.5
    y = checkPageBreak(doc, y, Math.min(estimatedHeight, 40), margin, companyName)

    // Section title
    doc.setFontSize(14)
    doc.setFont(FONT_NAME, 'bold')
    doc.setTextColor(...HEADING_COLOR)
    doc.text(label, margin, y)

    // Dashed underline — only as wide as the title text, like border-bottom in the editor
    const titleWidth = doc.getTextWidth(label)
    doc.setDrawColor(...LIGHT_BORDER)
    doc.setLineWidth(0.4)
    doc.setLineDashPattern([1.2, 1.2], 0)
    doc.line(margin, y + 2, margin + titleWidth, y + 2)
    doc.setLineDashPattern([], 0) // reset to solid
    // intro/summary have no subheadings so need more breathing room after the title
    y += section.id === 'intro' || section.id === 'summary' ? 9.5 : 6.5

    // Body lines
    const bodyFontSize = 9.5
    const lineH = 5.2
    const subheadingLineH = 5.8
    const bulletIndent = margin + 5
    const bulletContentWidth = contentWidth - 5

    for (const line of parsedLines) {
      if (line.kind === 'subheading') {
        y = checkPageBreak(doc, y, subheadingLineH + 4, margin, companyName)
        y += 1
        doc.setFontSize(bodyFontSize)
        doc.setFont(FONT_NAME, 'bold')
        doc.setTextColor(...SUBHEADING_COLOR)

        // subheadings may contain inline markup — strip it for simplicity
        const subText = line.text
          .replace(/\*\*(.+?)\*\*/g, '$1')
          .replace(/==(.+?)==/g, '$1')
          .replace(/<[^>]+>/g, '')

        const wrapped = doc.splitTextToSize(subText, contentWidth)

        for (const wline of wrapped) {
          y = checkPageBreak(doc, y, subheadingLineH, margin, companyName)
          doc.text(wline, margin, y)
          y += subheadingLineH
        }

        doc.setFont(FONT_NAME, 'normal')
      } else if (line.kind === 'bullet') {
        y = checkPageBreak(doc, y, lineH + 2, margin, companyName)
        // Bullet dot
        doc.setFontSize(bodyFontSize)
        doc.setFont(FONT_NAME, 'normal')
        doc.setTextColor(...BODY_TEXT)
        doc.text('\u2022', margin, y)
        const endY = renderSegments(doc, line.segments, bulletIndent, y, bulletContentWidth, bodyFontSize, lineH)
        y = endY + 1
      } else {
        // paragraph
        y = checkPageBreak(doc, y, lineH + 1, margin, companyName)
        const endY = renderSegments(doc, line.segments, margin, y, contentWidth, bodyFontSize, lineH)
        y = endY + 1
      }
    }

    y += 8 // gap between sections
  }

  // ── Footer on last page ─────────────────────────────────────────────────────

  addFooter(doc, companyName)

  // ── Page numbers ────────────────────────────────────────────────────────────

  const totalPages = doc.getNumberOfPages()

  for (let i = 2; i <= totalPages; i++) {
    // skip cover (page 1)
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setFont(FONT_NAME, 'normal')
    doc.setTextColor(...WHITE)
    doc.text(`${i - 1} / ${totalPages - 1}`, pageWidth / 2, pageHeight - 5, {align: 'center'})
  }

  doc.save(`${companyName.replace(/\s+/g, '_')}_expense_policy.pdf`)
}
